import { BCMSRecord, useGlobalController, useModel, useParams, useRoute, Workflow } from "@bcm-dev/bcms-vite";
import { Account, Bill, Payment } from "model";
import React, { useState } from "react";
import MultiPayItem from "./classes/MultiPayItem";

import MultipayConfirmationPage from "compid/pages/MultipayConfirmationPage";
import SchedulePaymentsPage from "compid/pages/SchedulePaymentsPage";

// Steps of the workflow
const steps = {
    "/schedulePayment" : <SchedulePaymentsPage />,
    "/multipayConfirmationPage" : <MultipayConfirmationPage />
};

/**
 * @function MultiPayWorkflow
 * @returns {React.Component}
 * @description The main component for the multipay workflow
 * @captureState Example of changing state in a capture.
 *  document.dispatchEvent(new CustomEvent("force-workflow-state", {
        detail : {
            value : {
                items: [
                     {"amount": 123, date: 2, account: 1 },
                     {"amount": 456, date: 3, account: 1 },
                ]
            }
        }
    }));
 */
export default function MultiPayWorkflow(props) {
    // Initalize a global controller and get the bill ids passed
    const controller = useGlobalController();
     // Param from URL provided by route determines which bill we are going to pay.
     // If a "stepID" is passed, that is also read.
    const { billIDs, stepID } = useParams();
    // Get the default value of the workflow
    const value = getDefaultMultiPay(controller, billIDs);

    // Define the constructor function for forcing state through the 'force-workflow-state' event
    const constructor = record => {
        const recordItems = record.items;
        const currItems = value.items;

        // Manually update each key in the "MultiPayItem"
        // since we cant merge using object literals with that class type. 
        // It has no "record" property like other class types defined in the model xml.
        for(var i = 0; i < currItems.length; i++) {
            const recordItem = recordItems[i];
            const currItem = currItems[i];

            for(let key in recordItem) {
                const val = recordItem[key];
                currItem[key]= val;
            }
        }
        
        return value;
    }

    return <Workflow value={ value } steps={ Object.values(steps) } step={ stepID } constructor={ constructor } />
}

/**
 * @function getWorkflowSteps
 * @returns {OBJECT}
 * @description Hook to get the steps of the multipay workflow
 */
export const getWorkflowSteps = () => steps;

/**
 * @function getDefaultMultiPay
 * @param {BCMSGlobalController} controller - Global controller instance
 * @param {STRING} billIDs - The bill ids passed through the parameter
 * @returns {OBJECT}
 * @note The billIDs parameter expects ids formatted as "1,2,3" since thats how they are being passed from the useParams
 * @description Function to create the default value of the mutipay workflow
 */
export const getDefaultMultiPay = (controller, billIDs) => {
    BCMSRecord.globalControllerFor([ Bill, Payment, Account ], controller);
     const { branding} = useModel();
    const ids = billIDs !== undefined ? billIDs.split(",") : [];
    const items = ids.map(id => {
        var multiPayItem = MultiPayItem.init(id);
        // If item has a biller with a "due" status, set the biller
        // amount as the default amount for the biller.
        // This will prefill the amount fields in Scheduled Payments.
        const paymentDate = multiPayItem.date;
        const paymentType = multiPayItem.bill.biller.paymentType;
        multiPayItem.withdrawnOnDate = Payment.getWithdrawOnDate(paymentDate, paymentType, branding);
        if(multiPayItem.bill.status.toLowerCase() === "due") {
            multiPayItem.amount = multiPayItem.bill.amount;
        }
        return multiPayItem;
    }, []);

    return { items };
};

const MultiPayContext = React.createContext(null);

/**
 * @function MultiPayContextWrapper
 * @returns {React.Component}
 * @description Wrapper to provide shared context for different widgets on the the PayBills page
 */
export function MultiPayContextWrapper(props) {
    const [ state, setState ] = useState([]);
    const navigate = useRoute();
    
    const controller = {
        state,

        "add" : value => setState([ ...state, value ]),
        "remove" : value => setState(state.filter(item => item !== value, [])),

        "toggleAdd" : (value, id) => value ? controller.add(id) : controller.remove(id),
        "reset" : () => setState([]),

        "submit": evt => navigate(`/multiPayWorkflow/${ state }`)
    };

    return (
        <MultiPayContext.Provider value={ controller }>
            { props.children }
        </MultiPayContext.Provider>
    );
}

/**
 * @function useMultiPay
 * @returns {React.Context}
 * @description Hook to access the context of the MultiPayContextWrapper
 */
export const useMultiPay = () => React.useContext(MultiPayContext);